class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.handleKeyup = this.handleKeyup.bind(this);
    this.handleDrawerClick = this.handleDrawerClick.bind(this);
    this.handleCartIconClick = this.handleCartIconClick.bind(this);
    this.handleCartIconKeydown = this.handleCartIconKeydown.bind(this);

    this.addEventListener('keyup', this.handleKeyup);

    /*
     * Use event delegation instead of binding directly
     * to #CartDrawer-Overlay.
     *
     * Your custom cart replaces the drawer's inner HTML
     * after AJAX updates, so the overlay element itself
     * can be replaced. Delegation keeps this working.
     */
    this.addEventListener('click', this.handleDrawerClick);

    this.setHeaderCartIconAccessibility();
  }

  handleKeyup(event) {
    if (event.code === 'Escape') {
      this.close();
    }
  }

  handleDrawerClick(event) {
    if (
      event.target &&
      event.target.id === 'CartDrawer-Overlay'
    ) {
      this.close();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) {
      return;
    }

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    /*
     * Prevent duplicate listeners if this method
     * is ever called again.
     */
    cartLink.removeEventListener(
      'click',
      this.handleCartIconClick
    );

    cartLink.removeEventListener(
      'keydown',
      this.handleCartIconKeydown
    );

    cartLink.addEventListener(
      'click',
      this.handleCartIconClick
    );

    cartLink.addEventListener(
      'keydown',
      this.handleCartIconKeydown
    );
  }

  handleCartIconClick(event) {
    event.preventDefault();

    this.open(
      document.querySelector('#cart-icon-bubble')
    );
  }

  handleCartIconKeydown(event) {
    const code = event.code
      ? event.code.toUpperCase()
      : '';

    if (
      code === 'SPACE' ||
      code === 'ENTER'
    ) {
      event.preventDefault();

      this.open(
        document.querySelector('#cart-icon-bubble')
      );
    }
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote =
      this.querySelector(
        '[id^="Details-"] summary'
      );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(
        cartDrawerNote
      );
    }

    /*
     * requestAnimationFrame is smoother and more
     * reliable than an arbitrary timeout.
     */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add(
          'animate',
          'active'
        );
      });
    });

    const handleTransitionEnd = () => {
      if (!this.classList.contains('active')) {
        return;
      }

      const containerToTrapFocusOn =
        this.classList.contains('is-empty')
          ? this.querySelector(
              '.drawer__inner-empty'
            )
          : this.querySelector(
              '#CartDrawer'
            );

      const focusElement =
        this.querySelector(
          '.drawer__inner'
        ) ||
        this.querySelector(
          '.drawer__close'
        );

      if (
        containerToTrapFocusOn &&
        focusElement &&
        typeof trapFocus === 'function'
      ) {
        trapFocus(
          containerToTrapFocusOn,
          focusElement
        );
      }
    };

    this.addEventListener(
      'transitionend',
      handleTransitionEnd,
      {
        once: true
      }
    );

    document.body.classList.add(
      'overflow-hidden'
    );
  }

  close() {
    this.classList.remove('active');

    if (
      typeof removeTrapFocus ===
      'function'
    ) {
      removeTrapFocus(
        this.activeElement
      );
    }

    document.body.classList.remove(
      'overflow-hidden'
    );
  }

  setSummaryAccessibility(
    cartDrawerNote
  ) {
    cartDrawerNote.setAttribute(
      'role',
      'button'
    );

    cartDrawerNote.setAttribute(
      'aria-expanded',
      'false'
    );

    const controlledElement =
      cartDrawerNote.nextElementSibling;

    if (
      controlledElement &&
      controlledElement.getAttribute('id')
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        controlledElement.id
      );
    }

    cartDrawerNote.addEventListener(
      'click',
      (event) => {
        const details =
          event.currentTarget.closest(
            'details'
          );

        event.currentTarget.setAttribute(
          'aria-expanded',
          details
            ? !details.hasAttribute('open')
            : false
        );
      }
    );

    if (
      cartDrawerNote.parentElement &&
      typeof onKeyUpEscape ===
      'function'
    ) {
      cartDrawerNote.parentElement.addEventListener(
        'keyup',
        onKeyUpEscape
      );
    }
  }

  renderContents(parsedState) {
    /*
     * Do not assume every response always contains sections.
     */
    if (
      !parsedState ||
      !parsedState.sections
    ) {
      return;
    }

    const drawerInner =
      this.querySelector(
        '.drawer__inner'
      );

    if (
      drawerInner &&
      drawerInner.classList.contains(
        'is-empty'
      )
    ) {
      drawerInner.classList.remove(
        'is-empty'
      );
    }

    this.classList.remove(
      'is-empty'
    );

    this.productId =
      parsedState.id;

    this.getSectionsToRender().forEach(
      (section) => {
        const sectionElement =
          section.selector
            ? document.querySelector(
                section.selector
              )
            : document.getElementById(
                section.id
              );

        if (!sectionElement) {
          return;
        }

        const sectionHtml =
          parsedState.sections[
            section.id
          ];

        if (!sectionHtml) {
          return;
        }

        const innerHTML =
          this.getSectionInnerHTML(
            sectionHtml,
            section.selector
          );

        if (
          typeof innerHTML ===
          'string'
        ) {
          sectionElement.innerHTML =
            innerHTML;
        }
      }
    );

    /*
     * Because overlay closing is delegated from
     * <cart-drawer>, we DO NOT have to reattach
     * an overlay listener here after every render.
     */

    this.setHeaderCartIconAccessibility();

    requestAnimationFrame(() => {
      this.open();
    });
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    if (!html) {
      return '';
    }

    const doc =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const element =
      doc.querySelector(selector);

    return element
      ? element.innerHTML
      : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    if (!html) {
      return null;
    }

    return new DOMParser()
      .parseFromString(
        html,
        'text/html'
      )
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement =
      element;
  }
}


/*
 * Avoid "custom element already defined"
 * errors if Shopify loads this asset twice.
 */
if (
  !customElements.get(
    'cart-drawer'
  )
) {
  customElements.define(
    'cart-drawer',
    CartDrawer
  );
}


class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}


/*
 * Same protection here.
 */
if (
  !customElements.get(
    'cart-drawer-items'
  )
) {
  customElements.define(
    'cart-drawer-items',
    CartDrawerItems
  );
}